import React, { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Button, Modal, Row, Space, Tag, Typography } from "antd";
import { ReloadOutlined, ShoppingCartOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import LeftOrderCreateModel from "./LeftOrderCreateModel";
import RightOrderCreateModel from "./RightOrderCreateModel";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { ordersService } from "../../../../services/orders-services";
import { ordersActions } from "../../../../store/slices/orders-slices";
import { createOrderActions } from "../../../../store/slices/create-order-slice/create.orders.slice";
import { groupCustomDishes } from "../../../../helpers/groupCustomDishes";

const paymentMethods = [
  { name: "Transfer", value: "Transfer" },
  { name: "Online", value: "Online" },
  { name: "Cash", value: "Cash" },
];

const OrdersCreateModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPacksLoading, setIsPacksLoading] = useState(false);
  const [isConfirmLoading, setIsConfirmLoading] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  // promo code input
  const [isPromoCodeLoading, setIsPromoCodeLoading] = useState(false);
  // bonus points input
  const [isBonusPointsLoading, setIsBonusPointsLoading] = useState(false);
  const { periods } = useSelector((store) => store.periods);
  const { menuFormat } = useSelector((store) => store.appSettings);

  const {
    userId,
    packId,
    daysOrdered,
    selectedDates,
    paymentMethod,
    appliedPromoCode,
    appliedPromoCodeId,
    bonusPointsToUse,
    pricingLoading,
    payableAmount,
    dishes,
    replacedProteinDishes,
    replacedSauceDishes,
    isCustomMode,
    customDishes,
  } = useSelector((store) => store.createOrder);

  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleOk = () => {
    setIsModalOpen(false);
  };
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // Get eligible period based on days count
  const getEligiblePeriod = useCallback(() => {
    if (periods.length === 0 || daysOrdered <= 0) return null;

    // If promo has TABLE rule with period, prefer that
    if (appliedPromoCode?.tableRuleApplied?.periodId) {
      const promoPeriod = periods.find(
        (p) => p.id === appliedPromoCode.tableRuleApplied.periodId
      );
      if (promoPeriod) return promoPeriod;
    }

    // Otherwise, find best eligible period
    const eligiblePeriods = periods.filter((p) => p.daysCount <= daysOrdered);
    if (eligiblePeriods.length === 0) return null;

    return eligiblePeriods.sort((a, b) => b.daysCount - a.daysCount)[0];
  }, [periods, daysOrdered, appliedPromoCode]);

  const { menuDishesPacksForDates } = useSelector((store) => store.orders);
  const packs = useMemo(
    () => menuDishesPacksForDates?.[0]?.packs || [],
    [menuDishesPacksForDates]
  );

  const buildCustomMeals = () => {
    const customPack = packs.find((p) => p.id === packId);
    const customMealId = customPack?.meals?.[0]?.id || "";
    const meals = [];

    Object.entries(customDishes).forEach(([date, dishesForDate]) => {
      const groups = groupCustomDishes(dishesForDate);
      groups.forEach((group) => {
        const mainDish = group.main.dish;
        const proteinSub = group.subDishes.find((s) => s.dish.type === "PROTEIN");
        const sauceSub = group.subDishes.find((s) => s.dish.type === "SAUCE");

        meals.push({
          mealId: customMealId,
          dishId: mainDish.id,
          date,
          type: mainDish.type || undefined,
          proteinDishId: proteinSub?.dish?.id || undefined,
          sauceDishId: sauceSub?.dish?.id || undefined,
        });
      });
    });
    return meals;
  };

  const handleCreateOrder = async () => {
    if (!userId || !packId || !paymentMethod || !daysOrdered) {
      toast.error(t("Please fill in all required fields (client, menu, payment method, days)"));
      return;
    }

    if (!isCustomMode) {
      const datesWithDishes = new Set(
        dishes?.map((d) => dayjs(d?.date).format("YYYY-MM-DD"))
      );
      const missingDates = selectedDates?.filter(
        (d) => !datesWithDishes.has(dayjs(d).format("YYYY-MM-DD"))
      ) ?? [];
      if (missingDates.length > 0) {
        const formatted = missingDates
          .map((d) => dayjs(d).format("ddd, D MMM"))
          .join(", ");
        toast.error(`No menu for: ${formatted}. Please add the menu.`);
        return;
      }
    }

    try {
      setIsConfirmLoading(true);
      const eligiblePeriod = getEligiblePeriod();

      let meals;
      if (isCustomMode) {
        meals = buildCustomMeals();
      } else {
        meals = dishes.map((dish) => {
          const formattedDate = dish.date;
          const proteinReplacement = replacedProteinDishes.find(
            (r) =>
              r.mealId === dish.mealId &&
              r.date === formattedDate &&
              r.packId === packId
          );
          const sauceReplacement = replacedSauceDishes.find(
            (r) =>
              r.mealId === dish.mealId &&
              r.date === formattedDate &&
              r.packId === packId
          );

          return {
            mealId: dish.mealId,
            dishId: dish.dish.id,
            date: dish.date,
            type: dish.type || undefined,
            proteinDishId: proteinReplacement
              ? proteinReplacement.replacedTo
              : dish.proteinDish?.id || undefined,
            sauceDishId: sauceReplacement
              ? sauceReplacement.replacedTo
              : dish.sauceDish?.id || undefined,
          };
        });
      }

      const orderData = {
        packId: packId,
        daysOrdered: daysOrdered,
        clientId: userId,
        paymentMethod: paymentMethod,
        paymentAmount: payableAmount.toString(),
        meals,
        pricePerDay: daysOrdered > 0 ? Math.floor(payableAmount / daysOrdered) : 0,
        ...(eligiblePeriod && { periodId: eligiblePeriod.id }),
        ...(appliedPromoCode && { promoCodeId: appliedPromoCodeId }),
        bonusDiscount: bonusPointsToUse,
      };

      await ordersService.createOrder(orderData);
      // const orderId = res.order?.id || res.id;

      toast.success(t("Order created successfully"));
      dispatch(ordersActions.changeTrigger());
      dispatch(createOrderActions.resetCreateOrder());
      setIsModalOpen(false);
    } catch (error) {
      console.error(t("Failed to create order"), error);
      toast.error(t("Failed to create order"));
    } finally {
      setIsConfirmLoading(false);
    }
  };

  const isLoading = useMemo(() => {
    return (
      isPacksLoading ||
      isPromoCodeLoading ||
      isBonusPointsLoading ||
      isConfirmLoading ||
      pricingLoading
    );
  }, [
    isPacksLoading,
    isPromoCodeLoading,
    isBonusPointsLoading,
    isConfirmLoading,
    pricingLoading,
  ]);

  const modalTitle = (
    <Space align="center">
      <ShoppingCartOutlined style={{ fontSize: 18, color: "#1677ff" }} />
      <Typography.Text strong style={{ fontSize: 16 }}>
        {t("Create New Order")}
      </Typography.Text>
      <Tag color={menuFormat === "period_based" ? "blue" : "green"} style={{ margin: 0 }}>
        {menuFormat === "period_based" ? t("Cycle Menu") : t("Date Menu")}
      </Tag>
    </Space>
  );

  const modalFooter = [
    <Button
      key="refetch"
      icon={<ReloadOutlined />}
      onClick={() => setRefetchTrigger((n) => n + 1)}
      loading={isPacksLoading}
    >
      {t("Refetch")}
    </Button>,
    <Button key="cancel" onClick={handleCancel}>
      {t("Cancel")}
    </Button>,
    <Button
      key="submit"
      type="primary"
      loading={isLoading}
      onClick={handleCreateOrder}
    >
      {t("Create Order")}
    </Button>,
  ];

  return (
    <>
      <Button type="primary" onClick={showModal}>
        {t("button-create-text")}
      </Button>
      <Modal
        title={modalTitle}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={modalFooter}
        width={"85%"}
        maxWidth={"90%"}
        height={"75vh"}
        maxHeight={"75vh"}
      >
        <Row gutter={16}>
          <LeftOrderCreateModel
            setIsPacksLoading={setIsPacksLoading}
            setIsPromoCodeLoading={setIsPromoCodeLoading}
            setIsBonusPointsLoading={setIsBonusPointsLoading}
            isPacksLoading={isPacksLoading}
            isPromoCodeLoading={isPromoCodeLoading}
            isBonusPointsLoading={isBonusPointsLoading}
            refetchTrigger={refetchTrigger}
          />
          <RightOrderCreateModel />
        </Row>
      </Modal>
    </>
  );
};
export { OrdersCreateModal };
